// @ts-strict-ignore
import moment from 'moment';
import mapKeys from 'lodash/mapKeys';
import omit from 'lodash/omit';

import {BookmarksEntryV2Response, PostV2Request} from 'app_api/projects/bookmarks/types';
import {apiClient} from 'common/util/api-client';
import {savedReportsLimitStore} from 'common/report/stores/saved-reports-limit';
import type {DashboardOrigin} from 'common/report/util/bookmark-overrides';
import {emitInfraTrackEvent} from 'common/util/dom';
import {diffFromNow, getNow, PERFORMANCE_EVENT_PREFIX} from 'common/util/timing';
import {BaseClient, QueryOptions} from 'common/report/queries/utils/base-client';
import type {Bookmark, BookmarkParamsUnionType} from 'common/report/dashboards/types';
import {getProjectId} from 'common/report/util';
import {ApiResponseError} from 'common/util/shared-client';

import {appApiUrl} from '../utils';

export enum BookmarkType {
  Addiction = `addiction`,
  Dashboard = `dashboard`,
  Formulas = `formulas`,
  Funnels = `funnels`,
  Insights = `insights`,
  JqlConsole = `jql-console`,
  Impact = `launch-analysis`,
  Retention = `retention`,
  RetentionFrequency = `retention-frequency`,
  Revenue = `revenue`,
  Segmentation = `segmentation`,
  Segmentation3 = `segmentation3`,
  Users = `users`,
  Cohorts = `cohort_users`,
  Flows = `flows`,
  Experiments = `experiments`,
  FeatureFlags = `feature-flags`,
  MetricTrees = `metric-trees`,
  AgentFlows = `agent-flows`,
}

export interface BookmarkSaveParams {
  id?: number;
  // TODO: Make this a required field
  type?: BookmarkType;
  name: string;
  params: string;
  description?: string;
  icon?: string;
  global_access_type?: string;
  // deleted=false indicates undo soft-delete
  // deleted=true (or any other value) will be rejected by backend
  deleted?: false;
  dashboard_id?: number;
  bookmark_url_slug?: string;
  prev_bookmark_url_slug?: string;
  /** When set, the save is recorded as a revert to this historical snapshot slug. */
  reverted_to_params_version_id?: string;
}

interface BookmarkListParams {
  id?: number[];
  type?: BookmarkType;
  includeDashboardCards?: boolean;
  eligible_for_dashboards?: boolean;
}

interface BookmarkGetParams {
  id: number;
  type?: BookmarkType;
}

interface BookmarkDeleteParams {
  id: number;
}

/** Frontend bookmark type that transforms keys from the API response
 * @deprecated use Bookmark type instead. There's no real reason to have two types in the codebase
 **/
interface SavedBookmark<BookmarkParamsType> {
  created: string;
  dashboard?: DashboardOrigin;
  deleted?: string;
  description?: string;
  icon: string;
  id: number;
  includeInDashboard?: boolean;
  isDefault: boolean;
  modified: moment.Moment;
  modifiedStr?: string;
  name: string;
  originalType: string;
  params: BookmarkParamsType;
  projectId: number;
  title: string;
  type: BookmarkType;
  userId: number;
  user: string; // TODO @evnp - eliminate this confusing attr
  // TODO @evnp make these required once added to backend
  userName?: string;
  userEmail?: string;

  // permissions
  can_view?: boolean;
  can_update_basic?: boolean;
  can_share?: boolean;
  is_superadmin?: boolean;
  allow_staff_override?: boolean;
}

const BOOKMARK_KEY_MAP = Object.freeze({
  is_default: `isDefault`,
  original_type: `originalType`,
  project_id: `projectId`,
  user_id: `userId`,
  user_name: `userName`,
  user_email: `userEmail`,
  include_in_dashboard: `includeInDashboard`,
});

export interface Scope {
  projectId: number | null;
  workspaceId?: number | null;
}

export const bookmarkClient = {
  createBookmark,
  listBookmarks,
  getLinkedDashboardIds,
  deleteBookmark,
};

function getBaseEndpoint({projectId, workspaceId}: Scope): string {
  return appApiUrl(`bookmarks`, {projectId, workspaceId});
}

/** @deprecated use BookmarkStore.post() instead */
async function createBookmark<BookmarkParamsType>({
  scope,
  params,
}: {
  scope: Scope;
  params: BookmarkSaveParams;
}): Promise<SavedBookmark<BookmarkParamsType>> {
  const {id, ...validParams} = params;
  const url = getBaseEndpoint(scope);
  try {
    return formatBookmark(await apiClient.post(url, validParams));
  } finally {
    if (scope.projectId) {
      savedReportsLimitStore.refresh({projectId: scope.projectId}); // async, no need to wait
    }
  }
}

async function listBookmarks<BookmarkParamsType>({
  scope,
  params,
}: {
  scope: Scope;
  params: BookmarkListParams;
}): Promise<Array<SavedBookmark<BookmarkParamsType>>> {
  const url = getBaseEndpoint(scope);
  const bookmarks: Array<SavedBookmark<BookmarkParamsType>> = await apiClient.get(url, params, {}, `bracket`);
  const startedAt = getNow();
  const formattedBookmarks = bookmarks.map(formatBookmark) as Array<SavedBookmark<BookmarkParamsType>>;

  // profiling shows this is long running in insights lets measure before changing
  emitInfraTrackEvent(window, {
    eventName: `listBookmarks result processing`,
    eventPrefix: PERFORMANCE_EVENT_PREFIX,
    props: {
      elapsedMs: diffFromNow(startedAt),
      numItems: bookmarks.length,
    },
  });
  return formattedBookmarks;
}

export async function bulkUpdateBookmarks<BookmarkParamsType extends BookmarkParamsUnionType>(
  bookmarks: Array<Bookmark<BookmarkParamsType>>,
): Promise<TODO> {
  const params = {
    bookmarks: bookmarks.map((bookmark) => ({
      ...bookmark,
      params: JSON.stringify(bookmark.params),
    })),
  };
  return apiClient.post(getBaseEndpoint({projectId: Number.parseInt(getProjectId(), 10)}) + `/bulk-update`, params);
}

function getLinkedDashboardIds({scope, params}: {scope: Scope; params: {id: number}}): Promise<number[]> {
  const url = `${getBaseEndpoint(scope)}/${params.id}/linked-dashboard-ids`;
  return apiClient.get(url, {});
}

// TODO: replace this with a deleteBookmarkV2() method in /iron/common/report/stores/bookmarks.ts
async function deleteBookmark({scope, params}: {scope: Scope; params: BookmarkDeleteParams}): Promise<number> {
  const url = `${getBaseEndpoint(scope)}/${params.id}`;
  const result = await apiClient.delete(url);
  if (scope.projectId) {
    savedReportsLimitStore.refresh({projectId: scope.projectId}); // async, no need to wait
  }
  return result[`id`];
}

/**
 * @deprecated use raw Bookmark type instead of transforming into SavedBookmark
 */
function formatBookmark<BookmarkParamsType>(bookmark): SavedBookmark<BookmarkParamsType> {
  // Moment is used to correctly parse the modified date across different browsers;
  // before this additional fomatting, bookmark.modified is not understood by some browsers
  const utc = moment.utc(bookmark.modified).local();
  return {
    ...mapKeys(bookmark, (_, key) => BOOKMARK_KEY_MAP[key] || key),
    modified: utc,
    modifiedStr: utc.format(`MMM Do, YYYY`),
    params: JSON.parse(bookmark.params),
    title: bookmark.name,
  } as SavedBookmark<BookmarkParamsType>;
}

export interface BookmarkV2PatchParams<BookmarkParamsType = any> {
  id: number;
  type: BookmarkType;
  icon?: string;
  name?: string;
  params?: BookmarkParamsType;
  description?: string;
  is_visibility_restricted?: boolean;
  is_modification_restricted?: boolean;
  // deleted=false indicates undo soft-delete
  // deleted=true (or any other value) will be rejected by backend
  deleted?: false;
  dashboard_id?: number;
  bookmark_url_slug?: string;
  prev_bookmark_url_slug?: string;
}

// TODO: remove this deprecated BaseClient implementation, move to an API Resource implementation
// client for individual bookmarks
export class BookmarkV2Client extends BaseClient {
  private declare readonly baseEndpoint: string;

  constructor(projectScope: Scope) {
    super({cacheTtl: 0});
    this.baseEndpoint = getBaseEndpoint(projectScope).substring(1);
  }

  public buildQueryParams(params: BookmarkGetParams, queryOpts: QueryOptions): Record<string, any> {
    if ((queryOpts.method ?? `GET`) === `GET`) {
      return {type: params.type, v: 2, single_report: true};
    }
    return null;
  }

  public buildRequestBody(params: BookmarkV2PatchParams | PostV2Request, queryOpts: QueryOptions): null | string {
    if (queryOpts.method === `PATCH` || queryOpts.method === `POST`) {
      const bodyParams = omit(params, [`id`]);
      return JSON.stringify({...bodyParams, v: 2});
    }
    return null;
  }

  public buildEndpoint(
    params: BookmarkGetParams | BookmarkV2PatchParams | PostV2Request,
    queryOpts: QueryOptions,
  ): string {
    if (queryOpts.method === `POST`) {
      return this.baseEndpoint;
    }

    return `${this.baseEndpoint}/${params.id}`;
  }

  public processResponse(response): BookmarksEntryV2Response[`results`] {
    if (response.status === `error`) {
      throw new ApiResponseError(response, response.error);
    }
    return response.results;
  }
}
